import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import CollectionPage from "@/pages/CollectionPage";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import BecomeSeller from "@/pages/BecomeSeller";
import Affiliate from "@/pages/Affiliate";
import AffiliateDocs from "@/pages/AffiliateDocs";
import Marketplace from "@/pages/Marketplace";
import EducationHub from "@/pages/EducationHub";
import CourseRoute from "@/pages/CourseRoute";
import LivePage from "@/pages/LivePage";
import LiveRoom from "@/pages/LiveRoom";
import GreenRoom from "@/pages/GreenRoom";
import StageRoom from "@/pages/StageRoom";
import CreatorProfile from "@/pages/CreatorProfile";
import VideoFeed from "@/pages/VideoFeed";
import CreatorStudio from "@/pages/CreatorStudio";
import CoinsPage from "@/pages/CoinsPage";
import RewardsPage from "@/pages/RewardsPage";
import HelpCenter from "@/pages/HelpCenter";
import CertificateSuccess from "@/pages/CertificateSuccess";
import CertificateVerify from "@/pages/CertificateVerify";
import SebaraCard from "@/pages/SebaraCard";
import Contact from "@/pages/Contact";
import ExternalMarketplaces from "@/pages/ExternalMarketplaces";
import CorporateDocuments from "@/pages/CorporateDocuments";
import KnowledgeHub from "@/pages/KnowledgeHub";
import PolicyPage from "@/pages/PolicyPage";
import ShopPageView from "@/pages/ShopPageView";
import LegalCenter from "@/pages/LegalCenter";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import CommandCenter from "@/command-center/CommandCenter";
import YedaIOS from "@/pages/YedaIOS";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import TrackingCenter from "@/pages/TrackingCenter";
import AboutUs from "@/pages/AboutUs";
import MarketHome from "@/pages/market/MarketHome";
import MarketCollection from "@/pages/market/MarketCollection";
import MarketSearch from "@/pages/market/MarketSearch";
import MarketProduct from "@/pages/market/MarketProduct";
import MarketCart from "@/pages/market/MarketCart";
import OrderSuccess from "@/pages/market/OrderSuccess";
import { CjCartProvider } from "@/context/CjCartContext";
import { LanguageProvider } from "@/context/LanguageContext";
import TavenHub from "@/pages/TavenHub";
import Membership from "@/pages/Membership";
import MyMembership from "@/pages/MyMembership";
import BillingReturn from "@/pages/BillingReturn";
import Apply from "@/pages/Apply";
import SellerDashboard from "@/pages/SellerDashboard";
import SellerConsole from "@/pages/seller/SellerConsole";
import StorefrontPublic from "@/pages/StorefrontPublic";
import RetiredOffering from "@/pages/RetiredOffering";
import NotFound from "@/pages/NotFound";
import PlatformUpdate from "@/pages/PlatformUpdate";
import AccessDenied from "@/pages/AccessDenied";
import ProtectedRoute from "@/components/ProtectedRoute";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <CartProvider>
        <CjCartProvider>
        <LanguageProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/command-center" element={<CommandCenter />} />
            <Route path="/yeda-ios" element={<YedaIOS />} />
            {/* TEMPORARY: public platform-update notice takes over the front page.
                To restore the storefront home, remove this route and re-enable the
                <Route path="/" element={<Home />} /> line inside the Layout block below. */}
            <Route path="/" element={<PlatformUpdate />} />
            <Route element={<Layout />}>
              {/* TEMPORARY: front page replaced by <PlatformUpdate /> standalone route above.
                  Re-enable the line below to restore the storefront home. */}
              {/* <Route path="/" element={<Home />} /> */}
              <Route path="/shop" element={<Shop />} />
              <Route path="/market" element={<MarketHome />} />
              <Route path="/market/collection/:handle" element={<MarketCollection />} />
              <Route path="/market/search" element={<MarketSearch />} />
              <Route path="/market/product/:handle" element={<MarketProduct />} />
              <Route path="/market/cart" element={<MarketCart />} />
              <Route path="/market/order/success" element={<OrderSuccess />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/track" element={<TrackingCenter />} />
              <Route path="/collection/:handle" element={<CollectionPage />} />
              <Route path="/product/:handle" element={<ProductDetail />} />
              <Route path="/cart" element={<Navigate to="/market/cart" replace />} />
              <Route path="/legacy-cart" element={<CartPage />} />
              <Route path="/become-a-seller" element={<BecomeSeller />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/documents" element={<Marketplace />} />
              <Route path="/marketplace/documents/:slug" element={<Marketplace />} />
              <Route path="/affiliate-program" element={<Affiliate />} />
              <Route path="/affiliate-program/documents" element={<AffiliateDocs />} />
              <Route path="/affiliate-program/documents/:slug" element={<AffiliateDocs />} />
              <Route path="/education" element={<EducationHub />} />
              <Route path="/education/:slug" element={<CourseRoute />} />
              <Route path="/live" element={<LivePage />} />
              <Route path="/live/greenroom/:sessionId" element={<GreenRoom />} />
              <Route path="/live/stage/:sessionId" element={<StageRoom />} />
              <Route path="/live/:sessionId" element={<LiveRoom />} />
              <Route path="/creator/:email" element={<CreatorProfile />} />
              <Route path="/videos" element={<VideoFeed />} />
              <Route path="/coins" element={<CoinsPage />} />
              <Route path="/coins/success" element={<CoinsPage />} />
              <Route path="/rewards" element={<RewardsPage />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/certificate/success" element={<CertificateSuccess />} />
              <Route path="/verify/:certificateId" element={<CertificateVerify />} />
              <Route path="/verify" element={<CertificateVerify />} />
              <Route path="/knowledge-hub" element={<KnowledgeHub />} />
              <Route path="/sebara-virtual-card" element={<SebaraCard />} />
              <Route path="/sell-on-other-marketplaces" element={<ExternalMarketplaces />} />
              <Route path="/corporate-documents" element={<CorporateDocuments />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/policy/:slug" element={<PolicyPage />} />
              <Route path="/page/:handle" element={<ShopPageView />} />
              <Route path="/legal" element={<LegalCenter />} />
              <Route path="/legal/:slug" element={<LegalCenter />} />
              <Route path="/hub" element={<TavenHub />} />
              {/* Permanently retired public commerce surfaces — render a branded
                  410 "gone" page (never a silent redirect to Home). /wholesale/quotes
                  is retired here too: its UI workflow is no longer a live offering
                  (records remain server-side for audit only). */}
              <Route path="/dropshipping/*" element={<RetiredOffering />} />
              <Route path="/wholesale/*" element={<RetiredOffering />} />
              <Route path="/apply" element={<ProtectedRoute><Apply /></ProtectedRoute>} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/membership/billing/success" element={<BillingReturn mode="success" />} />
              <Route path="/membership/billing/cancel" element={<BillingReturn mode="cancel" />} />
              <Route path="/my-membership" element={<ProtectedRoute><MyMembership /></ProtectedRoute>} />
              <Route path="/seller-dashboard" element={<ProtectedRoute roles={["seller", "admin", "super_admin"]}><SellerDashboard /></ProtectedRoute>} />
              <Route path="/seller/console" element={<ProtectedRoute roles={["seller", "admin", "super_admin"]}><SellerConsole /></ProtectedRoute>} />
              <Route path="/studio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="/s/:slug" element={<StorefrontPublic />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </LanguageProvider>
        </CjCartProvider>
        </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
